import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";
import { Button, Icon, IconButton, Modal } from "components/hds";

import BmsAppointmentsPanel from "components/BmsAppointments/BmsAppointmentsPanel";
import BmsBookingFrame from "components/BmsAppointments/BmsBookingFrame";
import useBmsAppointments from "hooks/useBmsAppointments";
import { useAuth } from "contexts/Auth";
import { useCurrentProject } from "contexts/Projects";
import { useScenes } from "contexts/Scenes";

// Opens the BMS appointments modal. Only BMS b2c endcustomers get it: BMS
// ProfiPartners neither have nor book appointments, and non-BMS users have no
// business seeing a Calenso link at all.
//
// Telling those apart needs care. `isB2cUser()` does NOT work here —
// ProfiPartner accounts are `scope: 'b2c'` too (see the scope comment in the
// server's user.model.js), and `userData.customerNumber`, the actual
// ProfiPartner marker, is not part of the /users/me payload.
//
// What does work: on a BMS project the endcustomer is by definition the person
// whose own address is the project's `endcustomerEmail`. The ProfiPartner who
// created the project never matches it; the endcustomer always does. That also
// keeps the check to data the client already holds, with no extra endpoint.
const BmsAppointmentButton = () => {
	const { t } = useTranslation();
	const { me } = useAuth();
	const { current: currentScene } = useScenes();
	const currentProject = useCurrentProject();
	const [visible, setVisible] = useState(false);

	// Owned here rather than in the panel: the refresh control sits in the
	// dialog's header, which the panel (its children) cannot reach; the panel is
	// handed what it renders. Keyed on `visible`, so opening the dialog is what
	// triggers the fetch — nothing is requested for the many users who never
	// open it.
	const { appointments, bookingLink, loading, loaded, available, refresh } =
		useBmsAppointments(visible);

	// Whether the dialog shows the embedded booking widget instead of the list.
	const [booking, setBooking] = useState(false);

	const close = () => {
		setVisible(false);
		setBooking(false);
	};

	// Back from the widget, the endcustomer has quite possibly just booked, so
	// the list is re-fetched rather than shown as it was before.
	const backToList = () => {
		setBooking(false);
		refresh();
	};

	const endcustomerEmail = currentProject?.endcustomerEmail?.toLowerCase();
	const isBmsEndcustomer = Boolean(
		endcustomerEmail &&
			me?.profile?.email?.some(
				(email) => email.toLowerCase() === endcustomerEmail,
			),
	);

	// Mirrors ShareButton: the header grid places children by position, so an
	// empty span has to hold the column open rather than rendering nothing.
	if (!currentScene || !isBmsEndcustomer) {
		return <span>{/* Do not delete this span */}</span>;
	}

	return (
		<>
			<Button
				className="appointmentButton"
				inline
				icon={<Icon name="CalendarIcon" />}
				onClick={() => setVisible(true)}
			>
				{String(t("app.BmsAppointments.button"))}
			</Button>
			{/* The design-system dialog (hds), not the antd wrapper: it carries
			    §8's width, padding, radius, shadow and — what prompted the
			    switch — one standard close button, instead of the antd one this
			    file used to re-style per modal. It unmounts its children when
			    closed, so the panel re-fetches every time it is opened, which is
			    what someone reopening it after booking elsewhere expects.

			    Booking happens in this same dialog: it widens and shows the
			    Calenso widget in place of the list. Same dialog rather than a
			    second one on top, so there is never a stack of two scrims and
			    two close buttons. */}
			<StyledModal
				open={visible}
				onClose={close}
				$wide={booking}
				title={String(
					t(
						booking
							? "app.BmsAppointments.panel.bookButton"
							: "app.BmsAppointments.panel.title",
					),
				)}
				headerAction={
					// Back sits on the title line, not in a footer: the
					// Calenso widget brings its own buttons at the bottom of
					// the frame, and a second row of ours beneath them would
					// compete with them.
					booking ? (
						<BackButton
							inline
							icon={<Icon name="ArrowLeftIcon" />}
							onClick={backToList}
						>
							{String(t("app.global.btn.back"))}
						</BackButton>
					) : (
						<RoundIconButton
							label={String(t("app.BmsAppointments.panel.refresh"))}
							tooltip={{
								text: String(
									t("app.BmsAppointments.panel.refresh"),
								),
							}}
							loading={loading}
							onClick={() => refresh()}
						>
							<Icon name="RefreshIcon" />
						</RoundIconButton>
					)
				}
			>
				{/* No dialog footer: in the list view the booking button
				    belongs directly under the list, next to each appointment's
				    own manage button, so the panel owns both (see its comment);
				    in the booking view the widget has its own buttons there. */}
				{booking && bookingLink ? (
					<BmsBookingFrame bookingLink={bookingLink} />
				) : (
					<BmsAppointmentsPanel
						appointments={appointments}
						bookingLink={bookingLink}
						loaded={loaded}
						available={available}
						onBook={() => setBooking(true)}
					/>
				)}
			</StyledModal>
		</>
	);
};

// Wider than §8's 776px while the booking widget shows: Calenso lays its slot
// picker out in columns that get cramped below roughly 1000px. Doubled selector
// because the Dialog's own max-width sits at equal specificity.
const StyledModal = styled(Modal)<{ $wide: boolean }>`
	&& {
		max-width: ${({ $wide }) =>
			$wide ? "min(1100px, 100%)" : "var(--size-modal-width)"};
	}
`;

// Back from the booking widget, on the title line where the hds Modal puts
// header actions. The extra margin on top of the title row's 12px gap keeps it
// from reading as part of the heading. (Pushed over to the close button it was
// tried and looked worse.) The arrow drops to --size-icon-sm for the same
// reason as in BmsAppointmentsPanel's StyledLinkButton: the legacy glyph reads
// too heavy at the full 24px slot.
const BackButton = styled(Button)`
	margin-left: var(--space-4);

	.hds-btn__icon > svg {
		width: var(--size-icon-sm);
		height: var(--size-icon-sm);
	}
`;

// The system's icon button, rounded and sized to the close button beside it.
//
// IconButton is 32px with a 19px glyph — the toolbar tool size. Next to a title
// it read as the heavier of the two header controls, since the close button is
// an --size-icon slot at 24px. Matching that puts both on the same footing.
// Everything else — focus ring, hover, disabled, the loading spinner — stays
// the shared behaviour. Doubled selector because IconButton sets these same
// properties on the same element at equal specificity.
const RoundIconButton = styled(IconButton)`
	&& {
		width: var(--size-icon);
		height: var(--size-icon);
		border-radius: var(--radius-full);

		.hds-iconbtn__icon {
			width: var(--size-icon-sm);
			height: var(--size-icon-sm);
		}
	}
`;

export default BmsAppointmentButton;
